// Video Quality tab — thin adapters over valoi_engine's authenticated
// /api/v1/admin/quality/* API (see app/routers/admin_api.py, VideoQualityRepo).
// Same auth story as adminService.ts: every endpoint requires the caller's own Bearer
// JWT to belong to a role === 'admin' User; a non-admin gets a 403 ApiError.
//
// Internal only. The scores are written by valoi-engine-pipeline: by
// `evals.quality.history --save`, or by its worker when an admin presses "Score this
// video" (scoreQualityVideo). The engine only stores, serves and queues them, and lists
// completed videos nobody has scored yet with a null grade, so the tab covers every
// video rather than only the scored ones.

import { getJson, postJson } from '@/shared/api/apiClient';
import type { Page } from '@/shared/hooks/usePaginatedList';

const QUALITY_BASE = '/api/v1/admin/quality';

export type QualityGrade = 'pass' | 'warn' | 'fail' | 'skip';
export type QualityGradeFilter = QualityGrade | 'unscored';
export type QualitySort = 'worst' | 'newest';

function buildQuery(params: Record<string, string | number | null | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
}

// --- Summary -----------------------------------------------------------------

export type QualityIssue = { metric: string; category: string; fails: number; warns: number };
export type QualityKind = {
  modelName: string | null;
  style: string | null;
  scored: number;
  failPct: number;
  meanPenalty: number;
};
export type QualityWeek = { weekStart: string; passed: number; warned: number; failed: number };
export type QualitySummary = {
  completed: number;
  scored: number;
  grades: Record<QualityGrade, number>;
  topIssues: QualityIssue[];
  byKind: QualityKind[];
  weekly: QualityWeek[];
};

type QualitySummaryApiResponse = {
  completed: number;
  scored: number;
  grades: Partial<Record<QualityGrade, number>>;
  top_issues: QualityIssue[];
  by_kind: { model_name: string | null; style: string | null; scored: number; fail_pct: number; mean_penalty: number }[];
  weekly: { week_start: string; passed: number; warned: number; failed: number }[];
};

// GET /quality/summary
export async function getQualitySummary(): Promise<QualitySummary> {
  const body = await getJson<QualitySummaryApiResponse>(`${QUALITY_BASE}/summary`);
  return {
    completed: body.completed,
    scored: body.scored,
    grades: {
      pass: body.grades.pass ?? 0,
      warn: body.grades.warn ?? 0,
      fail: body.grades.fail ?? 0,
      skip: body.grades.skip ?? 0,
    },
    topIssues: body.top_issues,
    byKind: body.by_kind.map((k) => ({
      modelName: k.model_name,
      style: k.style,
      scored: k.scored,
      failPct: k.fail_pct,
      meanPenalty: k.mean_penalty,
    })),
    weekly: body.weekly.map((w) => ({
      weekStart: w.week_start,
      passed: w.passed,
      warned: w.warned,
      failed: w.failed,
    })),
  };
}

// --- Videos ------------------------------------------------------------------

export type QualityVideo = {
  jobId: string;
  title: string | null;
  createdAt: string;
  modelName: string | null;
  style: string | null;
  mode: string;
  /** Null for a completed video that has not been scored yet. */
  grade: QualityGrade | null;
  penalty: number | null;
  hardFailures: number | null;
  reasons: string[];
  scoredAt: string | null;
  rubricVersion: string | null;
};

type QualityVideoApiItem = {
  job_id: string;
  title: string | null;
  created_at: string;
  model_name: string | null;
  style: string | null;
  mode: string;
  grade: QualityGrade | null;
  penalty: number | null;
  hard_failures: number | null;
  reasons: string[];
  scored_at: string | null;
  rubric_version: string | null;
};

type QualityVideoListApiResponse = {
  total: number;
  limit: number;
  offset: number;
  items: QualityVideoApiItem[];
};

function toVideo(item: QualityVideoApiItem): QualityVideo {
  return {
    jobId: item.job_id,
    title: item.title,
    createdAt: item.created_at,
    modelName: item.model_name,
    style: item.style,
    mode: item.mode,
    grade: item.grade,
    penalty: item.penalty,
    hardFailures: item.hard_failures,
    reasons: item.reasons,
    scoredAt: item.scored_at,
    rubricVersion: item.rubric_version,
  };
}

// GET /quality/videos?grade=&sort=&limit=&offset=
export async function listQualityVideos(params: {
  grade?: QualityGradeFilter | null;
  sort?: QualitySort;
  limit?: number;
  offset?: number;
}): Promise<Page<QualityVideo>> {
  const qs = buildQuery({
    grade: params.grade,
    sort: params.sort,
    limit: params.limit,
    offset: params.offset,
  });
  const body = await getJson<QualityVideoListApiResponse>(`${QUALITY_BASE}/videos${qs}`);
  return { total: body.total, offset: body.offset, items: body.items.map(toVideo) };
}

export type QualityMetric = {
  name: string;
  value: number | null;
  grade: QualityGrade;
  category: 'hard' | 'threshold';
  unit: string;
  target: (number | null)[];
  acceptable: (number | null)[];
  why: string;
  /** Where to look when flagged, e.g. "scene 21 at 1:55 (strong)". */
  detail?: string | null;
};

export type QualityVideoDetail = QualityVideo & {
  metrics: QualityMetric[];
  inputs: string[];
  videoUrl: string | null;
};

type QualityVideoDetailApiResponse = QualityVideoApiItem & {
  metrics: QualityMetric[];
  inputs: string[];
  video_url: string | null;
};

// GET /quality/videos/{id}
export async function getQualityVideo(jobId: string): Promise<QualityVideoDetail> {
  const body = await getJson<QualityVideoDetailApiResponse>(
    `${QUALITY_BASE}/videos/${encodeURIComponent(jobId)}`,
  );
  return { ...toVideo(body), metrics: body.metrics, inputs: body.inputs, videoUrl: body.video_url };
}

// POST /quality/videos/{id}/score — ask the worker to score this one video. Returns once
// the score is queued; the grade shows on GET /quality/videos/{id} when it is done,
// usually within a minute or two (the worker downloads the video and analyses every
// scene). One video at a time, so nobody has to score every video to see one grade.
export async function scoreQualityVideo(jobId: string): Promise<void> {
  await postJson<unknown>(`${QUALITY_BASE}/videos/${encodeURIComponent(jobId)}/score`);
}
